import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { userReceivedAction } from '@root/entities/auth/atoms'
import {
  loginModel,
  registerWithTelegramRequestAction,
} from '@root/features/auth/authenticate'
import {
  loginWithTelegramDeeplinkEvent,
  registerWithTelegramDeeplinkEvent,
} from '@root/features/auth/telegram'
import { initializeUserAction } from '@root/features/initialization/atoms/common'
import { isTauriOrBrowserLocalAtom } from '@root/shared'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'

import type { OAuthCode, TelegramAuthToken } from '@root/entities/auth'
import type { InviteCode } from '@root/entities/referral'

/**
 * Signing in with Telegram, in a browser.
 *
 * The app's own sign-in is a state machine around a keychain: a password to
 * unlock it, a device key inside it. A browser has none of that, and Basic
 * does not need it — the keys are the server's. So the web keeps the halves
 * that matter and drops the rest: the bot says who somebody is, the API
 * turns that into a session, and what they may do next is the waiting list's
 * decision, not this one's.
 *
 * The app listens for the same two events and returns early on the web; this
 * returns early in the app. Between them every platform is answered once.
 */

interface ReturnedFromTelegram {
  code: OAuthCode
  token: TelegramAuthToken
  referralCode?: InviteCode | undefined
}

/**
 * Signs somebody up without asking them anything.
 *
 * No name, no password, no invite code: the terminal is invite-only by the
 * waiting list, and a second gate at the door would only ask people who are
 * already through the first one. The server makes a name out of what
 * Telegram calls them.
 */
export const signUpFromWebAction = action(
  async (ctx, { code, token, referralCode }: ReturnedFromTelegram) => {
    const result = await ctx.schedule(() =>
      registerWithTelegramRequestAction(ctx, {
        token,
        authCode: code,
        inviteCode: referralCode,
      }),
    )

    if (Result.isFailure(result)) {
      showErrorSnackbarAction(ctx, result.failure)
      return
    }

    userReceivedAction(ctx, result.success)
  },
  'signUpFromWebAction',
)

export const initializeWebSignInAction = action((ctx) => {
  // Whose session this is, if any. The browser keeps it in a cookie the app
  // cannot read, so it has to ask. The desktop app asks while unlocking its
  // keychain; without this the web treats every reload as a stranger — and
  // the queue screen reloads the page the moment somebody is let in.
  if (!ctx.get(isTauriOrBrowserLocalAtom)) {
    void initializeUserAction(ctx)
  }

  loginWithTelegramDeeplinkEvent.onCall((ctx, { code, token }) => {
    if (ctx.get(isTauriOrBrowserLocalAtom)) {
      return
    }
    loginModel.loginAction(ctx, {
      type: 'telegram',
      token,
      code,
      otp: undefined,
    })
  })

  registerWithTelegramDeeplinkEvent.onCall((ctx, payload) => {
    if (ctx.get(isTauriOrBrowserLocalAtom)) {
      return
    }
    void signUpFromWebAction(ctx, payload)
  })
}, 'initializeWebSignInAction')
